export default (node) => {
  const modal = node;
  const overlay = node.querySelector('[data-close-modal]');
  const closeButtons = node.querySelectorAll('[data-close-modal]');
  const form = node.querySelector('[data-notify-form]');
  const submitButton = node.querySelector('[data-notify-submit]');
  const formState = node.querySelector('[data-form-state]');
  const successState = node.querySelector('[data-success-state]');

  // Form fields
  const nameInput = node.querySelector('[data-input="name"]');
  const emailInput = node.querySelector('[data-input="email"]');
  const termsInput = node.querySelector('[data-input="terms"]');

  // Labels and errors
  const nameLabel = node.querySelector('[data-label="name"]');
  const emailLabel = node.querySelector('[data-label="email"]');
  const nameError = node.querySelector('[data-error="name"]');
  const emailError = node.querySelector('[data-error="email"]');
  const termsError = node.querySelector('[data-error="terms"]');
  const generalError = node.querySelector('[data-error="general"]');

  let currentVariantId = null;
  let isSubmitting = false;

  // Focus state management
  const handleFocus = (input, label) => {
    label.classList.add('focused');
    label.style.transform = 'translateY(-1.25rem)';
    label.style.fontSize = '12px';
    label.style.fontWeight = '700';
    label.style.color = '#383838'; // black from tailwind config
  };

  const handleBlur = (input, label) => {
    if (input.value === '') {
      label.classList.remove('focused');
      label.style.transform = '';
      label.style.fontSize = '';
      label.style.fontWeight = '';
      label.style.color = '';
    }
  };

  // Validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    let isValid = true;

    // Clear previous errors
    [nameError, emailError, termsError, generalError].forEach((error) => {
      if (error) error.classList.add('hidden');
    });

    // Validate name
    if (!nameInput.value.trim()) {
      nameError.textContent = 'Name is required';
      nameError.classList.remove('hidden');
      isValid = false;
    }

    // Validate email
    if (!emailInput.value.trim()) {
      emailError.textContent = 'Email is required';
      emailError.classList.remove('hidden');
      isValid = false;
    } else if (!validateEmail(emailInput.value)) {
      emailError.textContent = 'Please enter a valid email address';
      emailError.classList.remove('hidden');
      isValid = false;
    }

    // Validate terms
    if (!termsInput.checked) {
      termsError.textContent = 'Please accept the terms to continue';
      termsError.classList.remove('hidden');
      isValid = false;
    }

    return isValid;
  };

  // Modal controls
  const openModal = (variantId) => {
    currentVariantId = variantId;

    // Remove hidden class and add transition classes
    modal.classList.remove('notify-modal', 'hidden');
    modal.classList.add('notify-modal-visible', 'flex');
    document.body.style.overflow = 'hidden';

    // Reset form state
    resetForm();

    // Focus first input with slight delay for animation
    setTimeout(() => nameInput.focus(), 200);
  };

  const closeModal = () => {
    // Start closing animation
    modal.classList.remove('notify-modal-visible');
    modal.classList.add('notify-modal-closing');

    // Hide modal after animation completes
    setTimeout(() => {
      modal.classList.remove('notify-modal-closing', 'flex');
      modal.classList.add('notify-modal', 'hidden');
    }, 300);

    document.body.style.overflow = '';
    resetForm();
  };

  const resetForm = () => {
    // Reset form
    form.reset();

    // Reset states
    formState.classList.remove('hidden');
    successState.classList.add('hidden');

    // Reset labels
    [nameLabel, emailLabel].forEach((label) => {
      label.classList.remove('focused');
      label.style.transform = '';
      label.style.fontSize = '';
      label.style.fontWeight = '';
      label.style.color = '';
    });

    // Clear errors
    [nameError, emailError, termsError, generalError].forEach((error) => {
      if (error) error.classList.add('hidden');
    });

    // Reset button
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.textContent =
        submitButton.dataset.originalText || 'Notify Me';
    }

    isSubmitting = false;
  };

  // Klaviyo subscription
  const subscribeToKlaviyo = async (email, name, variantId) => {
    const companyId = window.shop?.klaviyoCompanyId;

    if (!companyId) {
      console.error('Klaviyo company ID not found');
      throw new Error('Configuration error');
    }

    const url = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${companyId}`;

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
              },
            },
          },
          channels: ['EMAIL'],
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variantId}`,
            },
          },
        },
      },
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: '2024-06-15',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return { success: true };
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!validateForm()) return;

    if (!currentVariantId) {
      console.error('No variant ID available');
      generalError.textContent = 'Product variant not found';
      generalError.classList.remove('hidden');
      return;
    }

    try {
      isSubmitting = true;

      // Update button state
      const originalText = submitButton.textContent;
      submitButton.dataset.originalText = originalText;
      submitButton.textContent = 'Submitting...';
      submitButton.disabled = true;

      await subscribeToKlaviyo(
        emailInput.value,
        nameInput.value,
        currentVariantId
      );

      // Show success state
      formState.classList.add('hidden');
      successState.classList.remove('hidden');

      // Close modal after delay
      setTimeout(() => {
        closeModal();
      }, 3000);
    } catch (error) {
      console.error('Subscription error:', error);
      generalError.textContent =
        'Sorry, something went wrong. Please try again.';
      generalError.classList.remove('hidden');

      // Reset button
      submitButton.textContent =
        submitButton.dataset.originalText || 'Notify Me';
      submitButton.disabled = false;
      isSubmitting = false;
    }
  };

  // Event listeners
  const init = () => {
    // Focus/blur handlers
    nameInput.addEventListener('focus', () =>
      handleFocus(nameInput, nameLabel)
    );
    nameInput.addEventListener('blur', () => handleBlur(nameInput, nameLabel));
    emailInput.addEventListener('focus', () =>
      handleFocus(emailInput, emailLabel)
    );
    emailInput.addEventListener('blur', () =>
      handleBlur(emailInput, emailLabel)
    );

    // Close modal handlers
    closeButtons.forEach((button) => {
      button.addEventListener('click', closeModal);
    });

    // Form submission
    form.addEventListener('submit', handleSubmit);

    // Listen for notify:open event
    document.addEventListener('notify:open', (e) => {
      const variantId = e.detail?.variantId;
      if (variantId) {
        openModal(variantId);
      } else {
        console.error('No variant ID provided in notify:open event');
      }
    });

    // Escape key handler
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.classList.contains('hidden')) {
        closeModal();
      }
    });
  };

  init();
};
